/**
 * Welchen Weg der Pfad meint.
 *
 * ## Kein Router
 *
 * Es gibt vier Pfade, die ohne Anmeldung aufgerufen werden — die geteilte
 * Reise, die Konto-Einladung, die Mitplaner-Einladung und die Hilfe. Für vier
 * Pfade eine Routing-Bibliothek
 * einzuziehen hieße, ein Problem zu lösen, das es hier nicht gibt: Es gibt
 * keine verschachtelten Ansichten, keine Parameterhierarchie und keinen
 * Verlauf, der zwischen ihnen hin- und herspringt. Wer den Link öffnet, bleibt
 * auf der einen Seite, die er geöffnet hat.
 *
 * ## Warum das nicht in App.svelte steht
 *
 * Weil eine Regex, die in einer Komponente eingebettet ist, nur mit Browser
 * prüfbar wäre. Genau hier lohnt der Test aber am meisten: Ein zu großzügiges
 * Muster fängt Pfade, die es nicht fangen soll, und ein zu enges lässt genau
 * die Tokens durchfallen, die der Server ausgibt. Beides sieht man dem
 * Ausdruck nicht an — man sieht es an Beispielen.
 *
 * Das Zeichenvorrat-Muster `[A-Za-z0-9_-]+` ist der Zeichenvorrat von
 * URL-sicherem Base64, in dem beide Tokens erzeugt werden.
 *
 * ## Warum hier die Kapitelliste der Hilfe hereinkommt
 *
 * Seit dem Handbuch (15.9.2026) ist `/hilfe/<kapitel>` ein Weg, und die
 * Erkennung muss wissen, welche Kapitel es gibt — sonst hielte sie jeden
 * Tippfehler hinter `/hilfe/` für eine Seite. Die Liste steht in
 * `hilfe/gliederung.ts` und hat keine eigenen Abhängigkeiten; die Richtung
 * bleibt damit einseitig.
 */
import { istKapitel } from './hilfe/gliederung';

/** Das Token hinter `/<weg>/<token>`, sonst `null`. Ein Schrägstrich am Ende ist erlaubt. */
function tokenAus(pfad: string, weg: string): string | null {
  const treffer = new RegExp(`^/${weg}/([A-Za-z0-9_-]+)/?$`).exec(pfad);
  return treffer ? treffer[1] : null;
}

/** `/geteilt/<token>` — die Reise, wie Freunde und Familie sie sehen. */
export function geteilterTokenAus(pfad: string): string | null {
  return tokenAus(pfad, 'geteilt');
}

/** `/einladung/<token>` — die Seite vor dem allerersten Login. */
export function einladungsTokenAus(pfad: string): string | null {
  return tokenAus(pfad, 'einladung');
}

/**
 * `/mitplanen/<token>` — die Einladung, an einer fremden Reise mitzuplanen.
 *
 * Derselbe Bau wie die beiden darüber und aus demselben Grund: Der Server
 * erzeugt das Token mit `secrets.token_urlsafe`, also im Zeichenvorrat von
 * URL-sicherem Base64.
 *
 * ## Warum das nicht `/einladung` ist
 *
 * Beide Seiten sagen „du bist eingeladen", meinen aber Verschiedenes: Die
 * Konto-Einladung schafft ein Konto, diese hier hängt ein **bestehendes** Konto
 * an eine Reise. Wer sie verwechselt, verwechselt sie an genau einer Stelle
 * folgenreich — der Mitplaner-Link führt ohne Konto nirgendwohin, und die
 * Konto-Einladung hilft dem, der schon eines hat, kein Stück weiter. Zwei Wege,
 * zwei Seiten, zwei Erklärungen.
 */
export function mitplanenTokenAus(pfad: string): string | null {
  return tokenAus(pfad, 'mitplanen');
}

/** Der Pfad der Hilfe — an einer Stelle, damit die drei Verweise nicht auseinanderlaufen. */
export const HILFEWEG = '/hilfe';

/**
 * `/hilfe` — die Anleitung, ohne Anmeldung.
 *
 * ## Warum ohne Anmeldung
 *
 * Wer eingeladen wird, will **vor** der ersten Anmeldung lesen, worauf er sich
 * einlässt — genau das ist der Grund, aus dem es die Einladungsseite gibt.
 * Schützenswert ist hier nichts: Auf dieser Seite stehen keine Reisen, keine
 * Namen und keine Zahlen, sondern nur, wie die Knöpfe heißen und in welcher
 * Reihenfolge man sie drückt. Eine Anmeldeschranke davor kostete Vertrauen und
 * schützte nichts.
 *
 * Wer angemeldet ist, bekommt dieselbe Seite. Zwei Fassungen derselben
 * Anleitung liefen auseinander, und die zweite wäre die schlechtere.
 *
 * ## Warum kein Token
 *
 * Die anderen beiden offenen Wege tragen eines, weil sie an *eine* Reise oder
 * *eine* Einladung gebunden sind. Die Hilfe ist an nichts gebunden — sie ist
 * für alle dieselbe, und ein Token wäre ein Schloss ohne Tür dahinter.
 *
 * ## Warum seit dem 15.9.2026 auch `/hilfe/<kapitel>`
 *
 * Die Hilfe ist ein Handbuch mit einem Kapitel je Seite. Der Pfad nennt das
 * Kapitel, damit ein Link auf ein Kapitel zeigen kann und nicht nur auf eine
 * Stelle in einer sehr langen Seite.
 *
 * Erkannt wird dabei **nur ein Kapitel, das es gibt** — nicht jeder
 * Unterpfad. Der Unterschied ist Absicht und wird geprüft
 * (`hilfe.test.mjs`): `/hilfe/tagesplan` ist kein Kapitel, sondern ein
 * Tippfehler oder ein alter Link, und eine Hülle, die darauf eine leere Seite
 * mit Leiste zeigte, behauptete, es gäbe dieses Kapitel. Wer eine unbekannte
 * Adresse öffnet, bekommt dieselbe Antwort wie auf jede andere unbekannte
 * Adresse dieser Anwendung.
 */
export function istHilfeweg(pfad: string): boolean {
  if (/^\/hilfe\/?$/.test(pfad)) return true;
  const id = hilfekapitelAus(pfad);
  return id !== null;
}

/**
 * Das Kapitel aus `/hilfe/<id>`, sonst `null`.
 *
 * Gibt `null` auch für `/hilfe` selbst zurück: Dort ist kein Kapitel genannt,
 * und die Hülle entscheidet selbst, womit sie aufmacht. Ein hier eingebauter
 * Rückfall auf das erste Kapitel wäre eine Gestaltungsentscheidung in einer
 * Pfadprüfung.
 */
export function hilfekapitelAus(pfad: string): string | null {
  const treffer = /^\/hilfe\/([a-z0-9-]+)\/?$/.exec(pfad);
  if (!treffer) return null;
  return istKapitel(treffer[1]) ? treffer[1] : null;
}

/**
 * Der Link auf ein Kapitel — `/hilfe/kosten`, oder `/hilfe` ohne Angabe.
 *
 * Ein unbekannter Anker landet auf der Übersicht und nicht auf einer Seite,
 * die es nicht gibt: Heute sind alle Sprungmarken der Hilfe Kapitelkennungen,
 * und ein erfundenes `/hilfe/irgendwas` wäre eine Adresse, die die Weiche
 * gar nicht als Hilfe erkennt (siehe `istHilfeweg`). Unteranker innerhalb
 * eines Kapitels fängt die Hülle selbst ab — sie weiß, welches Kapitel offen
 * ist, und dieser Weg hier weiß es nicht.
 */
export function hilfelink(anker?: string | null): string {
  if (!anker) return HILFEWEG;
  if (istKapitel(anker)) return `${HILFEWEG}/${anker}`;
  return HILFEWEG;
}

/** Der Pfad der Verwaltung — an einer Stelle, damit Link und Erkennung nicht auseinanderlaufen. */
export const VERWALTUNGSWEG = '/verwaltung/';

/**
 * `/verwaltung` — die Fläche des Betreibers.
 *
 * ## Kein Token, und trotzdem hier
 *
 * Die beiden anderen Wege tragen ein Token und sind deshalb ohne Anmeldung
 * aufrufbar. Dieser trägt keines und ist es nicht — er steht hier trotzdem,
 * weil die Erkennung eines Pfads dieselbe Sorte Fehler macht wie oben: Ein
 * `pfad.startsWith('/verwaltung')` fängt auch `/verwaltungsrat` und
 * `/verwaltung/nutzer/3`, und beides sieht man dem Ausdruck nicht an.
 *
 * ## Und was das ausdrücklich nicht ist
 *
 * Eine Rechteprüfung. Dass dieser Pfad nirgends verlinkt ist, solange das
 * Konto kein Admin ist, ist **keine Schutzmaßnahme** (§5 des Designsystems
 * sagt das genau so). Wer die Adresse eintippt, bekommt die Seite — sie ist
 * dann nur leer, weil der Server auf jede einzelne Anfrage dahinter mit 403
 * antwortet. Die Rechteprüfung trifft der Server, bei jeder Anfrage, und
 * nirgendwo sonst.
 */
export function istVerwaltungsweg(pfad: string): boolean {
  return /^\/verwaltung\/?$/.test(pfad);
}
